import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import {
  calculateDocumentSubtotal,
  getPublicInvoiceSample,
  type PublicInvoiceSample,
} from '../data/publicDocumentSamples';
import { adminPaymentLinkService } from '../services/adminPaymentLinkService';
import type { AdminPaymentLink } from '../services/adminPaymentLinkService';

const formatCurrency = (amount: number, currencyCode: string) =>
  amount.toLocaleString('fr-FR', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
  });

const formatDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const InvoiceDocument = () => {
  const { invoiceNumber } = useParams();
  const fallbackInvoice = useMemo(() => getPublicInvoiceSample(invoiceNumber), [invoiceNumber]);
  const [invoice, setInvoice] = useState<PublicInvoiceSample>(fallbackInvoice);
  const [paymentLink, setPaymentLink] = useState<AdminPaymentLink | null>(null);
  const [isRefreshingPaymentLink, setIsRefreshingPaymentLink] = useState(false);

  useEffect(() => {
    const key = (invoiceNumber || '').trim();
    if (!key.toLowerCase().startsWith('plink_')) {
      setInvoice(fallbackInvoice);
      setPaymentLink(null);
      return;
    }

    let cancelled = false;

    adminPaymentLinkService
      .getPublicPaymentLink(key)
      .then((paymentLink) => {
        if (cancelled) {
          return;
        }

        const items =
          paymentLink.lineItems.length > 0
            ? paymentLink.lineItems.map((item) => ({
                label: item.label,
                quantity: item.quantity,
                unitPrice: item.unitAmount,
              }))
            : [
                {
                  label: paymentLink.serviceName || 'Service',
                  quantity: 1,
                  unitPrice: paymentLink.amount || 0,
                },
              ];

        setInvoice({
          invoiceNumber: key.toUpperCase(),
          invoiceDate: paymentLink.createdAt || new Date().toISOString(),
          clientName: paymentLink.clientName || '',
          clientEmail: paymentLink.clientEmail || '',
          clientCompany: paymentLink.clientCompany || '',
          serviceName: paymentLink.serviceName || 'Service',
          currencyCode: paymentLink.currencyCode || 'EUR',
          paymentMethod:
            paymentLink.paymentMode === 'recurring'
              ? 'Abonnement récurrent'
              : 'Lien de paiement Mollie',
          items,
          taxAmount: 0,
        });
        setPaymentLink(paymentLink);
      })
      .catch(() => {
        if (!cancelled) {
          setInvoice(fallbackInvoice);
          setPaymentLink(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [invoiceNumber, fallbackInvoice]);

  const subtotal = calculateDocumentSubtotal(invoice.items);
  const total = subtotal + invoice.taxAmount;
  const isPaid = paymentLink?.sessionStatus === 'paid';
  const isExpired = paymentLink?.sessionStatus === 'expired';
  const quoteUrl = paymentLink?.quoteUrl || (paymentLink ? `/devis/${encodeURIComponent(paymentLink.paymentLinkKey)}` : '');
  const paymentUrl = paymentLink?.paymentUrl || '';

  const handleRefreshExpiredPaymentLink = async () => {
    if (!paymentLink || isRefreshingPaymentLink) {
      return;
    }

    setIsRefreshingPaymentLink(true);
    try {
      const response = await fetch('/api/public/payment-link-refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          key: paymentLink.paymentLinkKey,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.paymentLink?.paymentUrl) {
        throw new Error(payload?.error || 'Impossible de regenerer le lien.');
      }
      window.location.href = payload.paymentLink.paymentUrl;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Impossible de regenerer le lien de paiement.';
      alert(message);
    } finally {
      setIsRefreshingPaymentLink(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{invoice.invoiceNumber} - Facture | Couteau Suisse Digital</title>
        <meta
          name="description"
          content="Consultez votre facture Couteau Suisse Digital, ses lignes de facturation et les informations de règlement."
        />
      </Helmet>

      <main className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="overflow-hidden rounded-3xl bg-white shadow-xl"
          >
            <div className="border-b border-[#f1ece8] bg-gradient-to-b from-[#f7f3ef] to-[#fffaf7] px-8 py-10">
              <div className="text-sm font-semibold uppercase tracking-[0.18em] text-neon-orange">
                Facture
              </div>
              <h1 className="mt-3 text-4xl font-bold text-gray-900">{invoice.invoiceNumber}</h1>
              <p className="mt-4 max-w-2xl text-lg text-gray-600">
                Document récapitulatif de facturation pour{' '}
                <strong>{invoice.serviceName}</strong>.
              </p>
            </div>

            <div className="grid gap-8 px-8 py-8 lg:grid-cols-[minmax(0,1.2fr)_320px]">
              <div className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
                    <h2 className="mb-4 text-lg font-semibold text-gray-900">Facturé à</h2>
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="font-semibold text-gray-900">{invoice.clientName}</div>
                      <div>{invoice.clientCompany}</div>
                      <div>{invoice.clientEmail}</div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
                    <h2 className="mb-4 text-lg font-semibold text-gray-900">Informations</h2>
                    <div className="space-y-3 text-sm text-gray-700">
                      <div className="flex justify-between gap-4">
                        <span>Date</span>
                        <strong>{formatDate(invoice.invoiceDate)}</strong>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span>Mode de paiement</span>
                        <strong>{invoice.paymentMethod}</strong>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span>Prestation</span>
                        <strong className="text-right">{invoice.serviceName}</strong>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="overflow-hidden rounded-2xl border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-[#fff4eb]">
                      <tr className="text-left text-sm font-semibold text-[#7c4a22]">
                        <th className="px-6 py-4">Description</th>
                        <th className="px-6 py-4 text-center">Qté</th>
                        <th className="px-6 py-4 text-right">Prix</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {invoice.items.map((item) => (
                        <tr key={item.label}>
                          <td className="px-6 py-4 text-sm text-gray-900">{item.label}</td>
                          <td className="px-6 py-4 text-center text-sm text-gray-700">
                            {item.quantity}
                          </td>
                          <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                            {formatCurrency(item.quantity * item.unitPrice, invoice.currencyCode)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-5">
                <div className="rounded-2xl border border-[#f6d8c0] bg-[#fff4eb] p-6">
                  <div className="space-y-3 text-sm text-gray-700">
                    <div className="flex justify-between gap-4">
                      <span>Sous-total</span>
                      <strong>{formatCurrency(subtotal, invoice.currencyCode)}</strong>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span>Taxes</span>
                      <strong>{formatCurrency(invoice.taxAmount, invoice.currencyCode)}</strong>
                    </div>
                    <div className="flex justify-between gap-4 text-base">
                      <span>Total</span>
                      <strong className="text-neon-orange">
                        {formatCurrency(total, invoice.currencyCode)}
                      </strong>
                    </div>
                  </div>
                </div>

                <div id="paiement" className="rounded-2xl border border-gray-200 bg-white p-6">
                  <h3 className="mb-3 text-lg font-semibold text-gray-900">
                    Informations de règlement
                  </h3>
                  <p className="text-sm leading-6 text-gray-600">
                    Pour toute question sur la facturation, les modalités de paiement ou une
                    mise à jour administrative, contacte directement notre équipe.
                  </p>
                  {paymentLink && (
                    <div className="mt-5 space-y-3">
                      {isPaid ? (
                        <div className="rounded-xl bg-emerald-100 px-4 py-3 text-sm font-medium text-emerald-700">
                          Cette facture est déjà réglée.
                        </div>
                      ) : isExpired ? (
                        <div className="space-y-3">
                          <div className="rounded-xl bg-orange-100 px-4 py-3 text-sm font-medium text-orange-700">
                            Cliquez ci-dessous pour payer maintenant.
                          </div>
                          <button
                            type="button"
                            onClick={handleRefreshExpiredPaymentLink}
                            disabled={isRefreshingPaymentLink}
                            className="inline-flex w-full items-center justify-center rounded-full bg-neon-orange px-6 py-4 font-semibold text-white hover:bg-[#FF4500] transition-colors disabled:opacity-60"
                          >
                            {isRefreshingPaymentLink
                              ? 'Preparation du paiement...'
                              : 'Payer maintenant'}
                          </button>
                        </div>
                      ) : paymentUrl ? (
                        <a
                          href={paymentUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex w-full items-center justify-center rounded-full bg-neon-orange px-6 py-4 font-semibold text-white hover:bg-[#FF4500] transition-colors"
                        >
                          Payer maintenant
                        </a>
                      ) : null}

                      {quoteUrl && (
                        <a
                          href={quoteUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex w-full items-center justify-center rounded-full border border-neon-orange px-6 py-4 font-semibold text-neon-orange hover:bg-neon-orange hover:text-white transition-colors"
                        >
                          Voir le devis associé
                        </a>
                      )}
                    </div>
                  )}
                  <div className="mt-5 flex flex-col gap-3">
                    <Link
                      to="/moyens-de-paiement"
                      className="font-semibold text-neon-orange transition-colors hover:text-[#FF4500]"
                    >
                      Voir les moyens de paiement
                    </Link>
                    <Link
                      to="/contact"
                      className="font-semibold text-gray-900 transition-colors hover:text-neon-orange"
                    >
                      Contacter notre équipe
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>
        </div>
      </main>
    </>
  );
};

export default InvoiceDocument;
